import { useMemo } from 'react';
import { filterRoutesByFeatureFlags, getFeatureFlagKeys } from '@/navigation/routes/routeFlags';
import { useFeatureFlags } from './useFeatureFlags';
import type { AppRouteDefinition } from '@/navigation/routes/routes';
import type { FeatureFlagResolveResult } from '@/shared/data/featureFlags';

type UseAppFeatureFlagsArgs = {
  routes: AppRouteDefinition[];
  token: string | null;
};

export const useAppFeatureFlags = ({
  routes,
  token,
}: UseAppFeatureFlagsArgs): {
  routeDefinitions: AppRouteDefinition[];
  featureFlags: FeatureFlagResolveResult['flags'] | null;
  featureFlagsLoading: boolean;
} => {
  const keys = useMemo(
    () =>
      getFeatureFlagKeys(routes, [
        'operations',
        'delivery',
        'users',
        'notifications',
        'organisations',
        'organisationSelector',
        'projectSelector',
        'deltas',
        'themeModeSwitch',
        'testsCloneTable',
        'suitesCloneTable',
        'pipelinesCloneTable',
      ]),
    [routes],
  );
  const featureFlagsQuery = useFeatureFlags({
    keys,
    token,
    enabled: Boolean(token),
    refetchOnWindowFocus: false,
    staleTimeMs: 300_000,
  });
  const featureFlagsLoading = featureFlagsQuery.isLoading;
  const resolvedFlags = featureFlagsQuery.isLoading ? null : featureFlagsQuery.flags;
  const routeDefinitions = useMemo(
    () => filterRoutesByFeatureFlags(routes, resolvedFlags),
    [resolvedFlags, routes],
  );

  return { routeDefinitions, featureFlags: resolvedFlags, featureFlagsLoading };
};
